<template>
  <div v-if="loadingPackages" class="loader center-middle">
    <loading-state :size="50" />
  </div>
  <div v-else-if="packagesStatus.isErrored">
    <error-state />
  </div>
  <form v-else @submit.prevent="submit">
    <FormPackageSelect
      v-model="fields.packageId"
      @input="onPackageSelect"
      :packages="packages"
      :plan-site-count="planSiteCount"
      :plan-term="planTerm"
      :app-id="planAppId"
      :disabled="isSubmitting"
    />
    <div class="text-right mt-5" v-if="summaryLoading && total > 0">
      <LoadingState />
    </div>
    <div v-else class="text-right mt-4">
      <div v-if="!isNegative(due)" class="my-2">
        <h3 class="text-primary d-inline mr-4">{{ $t("global.sales-tax") }}:</h3>
        <span>{{ `+ ${moneyFormat(tax)}` }}</span>
      </div>
      <div class="my-2">
        <h3 class="text-primary d-inline mr-4">
          {{ isNegative(due) ? $t("global.amount-credited") : $t("global.amount-due") }}:
        </h3>
        <span>{{ moneyFormat(isNegative(due) ? -due : due) }}</span>
      </div>
      <div v-if="!isNegative(due)" class="my-2">
        <h3 class="text-primary d-inline mr-4">{{ $t("global.total") }}:</h3>
        <span>{{ moneyFormat(total) }}</span>
      </div>
    </div>
    <div class="form-buttons">
      <Btn
        class="mr-2"
        variant="light"
        type="button"
        :disabled="isSubmitting"
        @click="cancel"
        data-test="cancel-plan-change-term-button"
      >
        {{ $t("global.cancel") }}
      </Btn>
      <Btn
        variant="primary"
        type="submit"
        :disabled="!isSubmittable()"
        :loading="isSubmitting"
        data-test="plan-change-term-button"
      >
        {{ $t("global.upgrade") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import ErrorState from "@/components/display/ErrorState";
import FormPackageSelect from "@/components/display/form/FormPackageSelect";
import LoadingState from "@/components/display/LoadingState";
import { moneyFormat } from "leatherman-js/utils/formatters/money";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { planUpgrade as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "PlanUpgradeForm",
  props: {
    isPlanTShirt: {
      type: Boolean,
      required: true,
    },
    package: {
      type: Object,
      required: true,
    },
    planAppId: {
      type: Number,
      required: true,
    },
    planId: {
      type: Number,
      required: true,
    },
    planName: {
      type: String,
      required: true,
    },
    planSiteCount: {
      type: Number,
      required: true,
    },
    planTerm: {
      type: Number,
      required: true,
    },
  },
  components: {
    Btn,
    ErrorState,
    FormPackageSelect,
    LoadingState,
  },
  setup(props, { emit, root }) {
    const isSubmitting = ref(false);
    const errors = ref(null);
    const upgradePrices = ref(null);
    const packages = computed(() => {
      if (!upgradePrices.value) {
        return [];
      }
      const pkgs = store.getters["packages/getList"];
      const upgradeableIds = upgradePrices.value.map(({ packageId }) => packageId);
      return pkgs.filter(({ id }) => upgradeableIds.includes(id));
    });
    const packagesStatus = computed(() => store.getters["packages/getListStatus"]);
    const fields = reactive({ packageId: props.package.id });
    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        term: {
          required: root.$t("validation.plan-required"),
        },
      }
    );
    const selectedPackage = computed(() =>
      store.getters["packages/getPackageById"](fields.packageId)
    );
    const tax = ref(0);
    const due = computed(() =>
      selectedPackage.value ? getUpgradePrice(selectedPackage.value) : 0
    );
    const total = computed(() => due.value + tax.value);
    const loadingUpgradePrices = ref(true);
    const loadingTax = ref(true);
    const loadingPackages = computed(
      () => packagesStatus.value.isLoading || loadingUpgradePrices.value
    );
    const summaryLoading = computed(() => loadingTax.value || loadingUpgradePrices.value);
    const onPackageSelect = () => {
      loadingTax.value = true;
      store
        .dispatch("plans/calculateUpgradeTax", {
          planId: props.planId,
          packageId: selectedPackage.value.id,
          appId: props.planAppId,
          term: props.planTerm === 1 ? "monthly" : "annually",
        })
        .finally(() => {
          loadingTax.value = false;
        });
    };
    const getUpgradePrice = (pkg) =>
      upgradePrices.value.find(({ packageId }) => packageId === pkg.id)?.amount || 0;
    const isNegative = (amount) => amount < 0;
    const cancel = () => emit("close");
    const isSubmittable = () =>
      !isSubmitting.value &&
      validation.errors.length === 0 &&
      Number(fields.packageId) !== props.package.id;

    const submit = () => {
      errors.value = null;

      validation
        .validate()
        .then(() => {
          isSubmitting.value = true;
          store
            .dispatch("plans/updatePackage", {
              id: props.planId,
              data: {
                packageId: fields.packageId,
              },
            })
            .then(() => {
              const getSelectedPackage = () =>
                packages.value.find((item) => item.id === fields.packageId);
              store.dispatch("toasts/addToast", {
                variant: "info",
                message: root.$t("toasts.plan-changing-package-message", {
                  name: props.planName,
                  planType: getSelectedPackage().name,
                }),
              });
              emit("close");
            })
            .catch(() => {
              store.dispatch("toasts/addToast", {
                variant: "error",
                message: root.$t("toasts.plan-change-package-error-message", {
                  name: props.planName,
                }),
              });
            })
            .finally(() => {
              isSubmitting.value = false;
            });
        })
        .catch(() => {});
    };

    onMounted(() => {
      store.dispatch("packages/getList");
      store
        .dispatch("checkout/calculateTax")
        .then((amount) => {
          tax.value = amount;
        })
        .finally(() => {
          loadingTax.value = false;
        });
      store
        .dispatch("plans/getUpgradePrices", {
          planId: props.planId,
          appId: props.isPlanTShirt ? undefined : props.planAppId,
          isTShirt: props.isPlanTShirt,
        })
        .then((list) => {
          upgradePrices.value = list;
        })
        .finally(() => {
          loadingUpgradePrices.value = false;
        });
    });

    return {
      errors,
      fields,
      isNegative,
      isSubmitting,
      submit,
      validation,
      cancel,
      isSubmittable,
      packages,
      packagesStatus,
      onPackageSelect,
      tax,
      due,
      total,
      loadingPackages,
      summaryLoading,
      moneyFormat,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/variables";
.form-buttons {
  text-align: right;
  padding-top: 2rem;
}
</style>
